import { motion } from 'framer-motion';
import { useState } from 'react';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Thank you! Your message has been sent successfully. We will contact you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to send message. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <MapPin className="w-6 h-6" />,
      title: 'Our Location',
      content: 'Mysore, Karnataka, India',
      link: 'https://maps.app.goo.gl/Vnrsa2DwJk3sGYoA6',
      type: 'map',
    },
    {
      icon: <Phone className="w-6 h-6" />,
      title: 'Phone Number',
      content: '+91 7760180840',
      link: 'tel:+917760180840',
      type: 'phone',
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: 'Email Address',
      content: 'powertechgeneratorsmysore@gmail.com',
      link: 'mailto:powertechgeneratorsmysore@gmail.com',
      type: 'email',
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: 'Business Hours',
      content: 'Mon - Sat: 9:00 AM - 6:00 PM',
      type: 'text',
    },
  ];

  return (
    <div className="pt-20" data-testid="contact-page">
      <section className="py-20 bg-gradient-to-br from-[#1B365D] to-[#2563eb] text-white noise-overlay" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" data-testid="contact-title">
              Get In Touch
            </h1>
            <p className="text-lg sm:text-xl text-slate-200 max-w-3xl mx-auto">
              Have questions about our diesel generators? We are here to help you find the perfect power solution.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-slate-50" data-testid="contact-info-cards">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-8 rounded-sm border border-slate-200 shadow-md text-center hover:border-[#F97316] hover:shadow-lg transition-all"
                data-testid={`contact-info-${index}`}
              >
                <div className="text-[#F97316] flex justify-center mb-4">{info.icon}</div>
                <h3 className="text-lg font-bold text-[#1B365D] mb-2">{info.title}</h3>
                {info.link ? (
                  <a 
                    href={info.link}
                    target={info.type === 'map' ? '_blank' : undefined}
                    rel={info.type === 'map' ? 'noopener noreferrer' : undefined}
                    className="text-slate-600 hover:text-[#F97316] transition-colors inline-block"
                  >
                    {info.content}
                  </a>
                ) : (
                  <p className="text-slate-600">{info.content}</p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1B365D] mb-6" data-testid="form-section-title">
                Send Us a Message
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                Fill out the form below and our team will get back to you within 24 hours. Whether you need a quote,
                technical support, or general information, we are here to assist you.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <Label htmlFor="contact-name" className="text-[#1B365D] font-semibold mb-2">
                    Full Name *
                  </Label>
                  <Input
                    id="contact-name"
                    data-testid="contact-form-name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter your full name"
                    className="rounded-sm border-slate-300 focus:ring-[#F97316] focus:border-[#F97316]"
                  />
                </div>

                <div>
                  <Label htmlFor="contact-email" className="text-[#1B365D] font-semibold mb-2">
                    Email Address *
                  </Label>
                  <Input
                    id="contact-email"
                    type="email"
                    data-testid="contact-form-email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your.email@example.com"
                    className="rounded-sm border-slate-300 focus:ring-[#F97316] focus:border-[#F97316]"
                  />
                </div>

                <div>
                  <Label htmlFor="contact-phone" className="text-[#1B365D] font-semibold mb-2">
                    Phone Number *
                  </Label>
                  <Input
                    id="contact-phone"
                    type="tel"
                    data-testid="contact-form-phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+91 XXXXXXXXXX"
                    className="rounded-sm border-slate-300 focus:ring-[#F97316] focus:border-[#F97316]"
                  />
                </div>

                <div>
                  <Label htmlFor="contact-message" className="text-[#1B365D] font-semibold mb-2">
                    Your Message *
                  </Label>
                  <Textarea
                    id="contact-message"
                    data-testid="contact-form-message"
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Tell us about your power requirements or questions..."
                    rows={6}
                    className="rounded-sm border-slate-300 focus:ring-[#F97316] focus:border-[#F97316]"
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="contact-form-submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold py-6 rounded-sm uppercase tracking-wide"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1B365D] mb-6" data-testid="map-section-title">
                Find Us Here
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Visit our office in Mysore or reach out to us for on-site consultations across Karnataka.
              </p>
              <div className="rounded-sm overflow-hidden shadow-xl border border-slate-200 mb-6" data-testid="google-map">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3898.0364476364247!2d76.62893507507627!3d12.307726787975803!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3baf70d388888889%3A0x4fb2e5d46e47c2f3!2sMysuru%2C%20Karnataka!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
                  width="100%"
                  height="500"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Power Tech Engineers Location"
                ></iframe>
              </div>
              <a
                href="https://maps.app.goo.gl/Vnrsa2DwJk3sGYoA6"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold py-3 px-6 rounded-sm transition-all"
                data-testid="view-on-maps-button"
              >
                <MapPin size={20} />
                View on Google Maps
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#1B365D] text-white noise-overlay" data-testid="emergency-contact">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6" data-testid="emergency-title">
              Need Emergency Support?
            </h2>
            <p className="text-lg text-slate-300 mb-8">
              Our 24/7 emergency response team is always ready to assist you with critical power issues.
            </p>
            <a
              href="tel:+917760180840"
              className="bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold py-4 px-10 rounded-sm transition-all uppercase tracking-wide inline-flex items-center gap-3"
              data-testid="emergency-call-button"
            >
              <Phone className="w-5 h-5" />
              Call Emergency Hotline: +91 7760180840
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};